/* global localStorage */

import {
  put,
  call,
  spawn,
  takeLatest,
  takeEvery,
  all,
  select } from 'redux-saga/effects';
import {
  ADMIN_FETCH_COWS_REQUEST,
  ADMIN_FETCH_COW_REQUEST,
  ADMIN_ADD_COW_REQUEST,
  ADMIN_UPDATE_COW_REQUEST,
  ADMIN_DELETE_COW_REQUEST,
  ADMIN_UPDATE_COW_UNDER_EDIT,
  ADMIN_REGISTER_REQUEST,
  ADMIN_REG_CONFIRM_REQUEST,
  ADMIN_SET_PWD_REQUEST,
  ADMIN_UPDATE_PWD_REQUEST,
  ADMIN_SLAUGHTER_CATTLE
} from './constants';
import {
  fetchAnimalsSuccess,
  fetchAnimalsFail,
  addAnimalSuccess,
  addAnimalFail,
  updateAnimalSuccess,
  updateAnimalFail,
  fetchAnimal,
  deleteAnimalSuccess,
  deleteAnimalFail,
  fetchAnimalFail,
  fetchAnimalSuccess,
  registerUserSuccess,
  registerUserFail,
  confirmRegistrationSuccess,
  confirmRegistrationFail,
  setNewPasswordFail,
  setNewPasswordSuccess,
  updatePasswordSuccess,
  updatePasswordFail,
  setAnimalEditErrors
} from './actions';
import { parseApiError } from '../util/api';
import { decodeJWT } from '../util/jwt';
import AdminAPI from './AdminAPI';
import { showNotification } from '../notifications/actions';
import history from '../goodBeefIndex/gbiHistory';
import { loginSuccess } from '../user/actions';
import ScoreAdminAPI from './scoreDefinitions/ScoreAdminAPI';
import { captureException } from '../util/logger';
import { getScoreDefinitions } from './scoreDefinitions/reducer';
import { getAnimalValidationErrors } from './validation/animal';

function *fetchAll () {
  try {
    const [ fetchAnimalsResponse, scoresResponse ] = yield all([
      call(AdminAPI.fetchCows),
      call(ScoreAdminAPI.fetchScores)
    ]);
    const scoreDefinitions = {};
    scoresResponse.data.scores.forEach(score => {
      scoreDefinitions[score.key] = score;
    });
    if (fetchAnimalsResponse.data &&
      Array.isArray(fetchAnimalsResponse.data.animals)) {
      yield put(fetchAnimalsSuccess(
        fetchAnimalsResponse.data.animals,
        scoreDefinitions,
        fetchAnimalsResponse.data.slimlineAnimals === true
      ));
    } else {
      let msg = 'unexpected response fetching animals';
      yield put(fetchAnimalFail(msg));
      yield put(showNotification(
        'Failed to load animals', msg, 'error', 10
      ));
    }
  } catch (error) {
    const msg = parseApiError(error);
    yield put(fetchAnimalsFail(msg));
    yield put(showNotification(
      'Failed to load animals', msg, 'error', 10
    ));
  }
}

function *fetch (action) {
  try {
    const [ fetchAnimalsResponse, scoresResponse ] = yield all([
      call(AdminAPI.fetchCow, action.id),
      call(ScoreAdminAPI.fetchScores)
    ]);
    const scoreDefinitions = {};
    scoresResponse.data.scores.forEach(score => {
      scoreDefinitions[score.key] = score;
    });
    if (fetchAnimalsResponse.data) {
      yield put(
        fetchAnimalSuccess(fetchAnimalsResponse.data, scoreDefinitions)
      );
    } else {
      let msg = 'unexpected response fetching animals';
      yield put(fetchAnimalFail(msg, action.id));
      yield put(showNotification(
        'Failed to load animal', msg, 'error', 10
      ));
    }
  } catch (error) {
    const msg = parseApiError(error);
    yield put(fetchAnimalFail(msg, action.id));
    yield put(showNotification(
      'Failed to load animal', msg, 'error', 10
    ));
  }
}

function *deleteS3Files (files) {
  try {
    if (files.length < 1) return;
    yield call(AdminAPI.deleteDocuments, files);
  } catch (error) {
    captureException(`error deleting documents. ${error.toString()}`);
  }
}

function *add (action) {
  try {
    yield call(AdminAPI.addCow, action.animal);
    yield spawn(deleteS3Files, action.cloudFilesToRemove);
    yield put(addAnimalSuccess(action.animal));
    yield put(showNotification(
      'Success',
      `${action.animal.name || action.animal.tag} saved`,
      'info',
      5
    ));
    if (action.redirectUrl) {
      history.push(action.redirectUrl);
    }
  } catch (error) {
    const msg = parseApiError(error);
    yield put(addAnimalFail(msg, action.animal));
    yield put(showNotification(
      `Failed to add animal ${action.animal.name || action.animal.tag}`,
      msg,
      'error',
      10
    ));
  }
}

function *update (action) {
  try {
    yield call(AdminAPI.updateCow, action.animal);
    yield spawn(deleteS3Files, action.cloudFilesToRemove);
    yield put(updateAnimalSuccess(action.animal));
    yield put(showNotification(
      'Success', `${action.animal.name || action.animal.tag} saved`, 'info', 5
    ));
  } catch (error) {
    const msg = parseApiError(error);
    yield put(updateAnimalFail(msg, action.animal));
    yield put(showNotification(
      `Failed to update animal ${action.animal.name || action.animal.tag}`,
      msg,
      'error',
      10
    ));
    yield put(fetchAnimal(action.animal.id));
  }
}

function *deleteAnimal (action) {
  try {
    yield call(AdminAPI.deleteCow, action.animal);
    yield spawn(deleteS3Files, action.cloudFilesToRemove);
    yield put(deleteAnimalSuccess(action.animal));
    yield put(showNotification(
      'Success', `${action.animal.name || action.animal.tag} deleted`, 'info', 5
    ));
  } catch (error) {
    const msg = parseApiError(error);
    yield put(deleteAnimalFail(msg, action.animal));
    yield put(showNotification(
      `Failed to delete animal ${action.animal.name || action.animal.tag}`,
      msg,
      'error',
      10
    ));
    yield put(fetchAnimal(action.animal.id));
  }
}

function *registerUser (action) {
  try {
    yield call(AdminAPI.registerUser, action.registration);
    yield put(registerUserSuccess());
    yield put(showNotification(
      'Account Created',
      `We've sent an email to ${action.registration.email} 
      with further instructions`,
      'success',
      15 // just keep it there.
    ));
  } catch (error) {
    yield put(registerUserFail());
    const msg = parseApiError(error);
    yield put(showNotification(
      `Error`, `Failed to register user: ${msg}`, 'error', 10
    ));
  }
}

function *confirmUserRegistration (action) {
  try {
    yield call(AdminAPI.confirmRegistration, action.authCode, action.id);
    yield put(confirmRegistrationSuccess());
    yield put(showNotification(
      'Registration confirmed',
      'please log in',
      'success',
      15
    ));
    history.push('/login');
  } catch (error) {
    const msg = parseApiError(error);
    yield put(confirmRegistrationFail());
    yield put(showNotification(
      'Error',
      msg,
      'error',
      10
    ));
  }
}

function *setPassword (action) {
  try {
    const { data } = yield call(
      AdminAPI.setPassword, action.authCode, action.id, action.password
    );
    localStorage.setItem('jwt', data.jwt);
    yield put(loginSuccess(decodeJWT(data.jwt)));
    yield put(setNewPasswordSuccess());
    history.push('/account');
  } catch (error) {
    const msg = parseApiError(error);
    yield put(setNewPasswordFail(msg));
    yield put(showNotification('Error', msg, 'error', 10));
  }
}

function *updatePassword (action) {
  try {
    yield call(
      AdminAPI.setPassword, action.email,
      action.existingPassword,
      action.newPassword
    );
    yield put(updatePasswordSuccess());
    history.push('/account');
  } catch (error) {
    const msg = parseApiError(error);
    yield put(updatePasswordFail(msg));
    yield put(showNotification('Error', msg, 'error', 10));
  }
}

export function *slaughterAnimals (action) {
  try {
    const { data } = yield call(
      AdminAPI.slaughterAnimals,
      action.animalIds,
      action.slaughterDate,
      action.cuttingDate,
      action.slaughterHouseID,
      action.cuttingPlantID
    );
    if (data && typeof data.animalsUpdated === 'number') {
      const s = data.animalsUpdated > 1 ? 's' : '';
      const msg = `${data.animalsUpdated} animal${s} updated`;
      yield put(showNotification('Recorded', msg, 'success', 10));
    } else {
      const msg = 'unexpected response from the server';
      yield put(showNotification('Error', msg, 'error', 10));
    }
  } catch (error) {
    const msg = parseApiError(error);
    yield put(showNotification('Error', msg, 'error', 10));
  }
}

function *validateAnimal (action) {
  const scoreDefinitions = yield select(getScoreDefinitions);
  const errors = yield call(
    getAnimalValidationErrors, action.animal, scoreDefinitions
  );
  yield put(setAnimalEditErrors(errors));
}

function *adminSagas () {
  yield takeLatest(ADMIN_FETCH_COWS_REQUEST, fetchAll);
  yield takeLatest(ADMIN_FETCH_COW_REQUEST, fetch);
  yield takeEvery(ADMIN_ADD_COW_REQUEST, add);
  yield takeEvery(ADMIN_UPDATE_COW_REQUEST, update);
  yield takeEvery(ADMIN_DELETE_COW_REQUEST, deleteAnimal);
  yield takeEvery(ADMIN_REGISTER_REQUEST, registerUser);
  yield takeEvery(ADMIN_REG_CONFIRM_REQUEST, confirmUserRegistration);
  yield takeEvery(ADMIN_SET_PWD_REQUEST, setPassword);
  yield takeEvery(ADMIN_UPDATE_PWD_REQUEST, updatePassword);
  yield takeEvery(ADMIN_SLAUGHTER_CATTLE, slaughterAnimals);
  yield takeLatest(ADMIN_UPDATE_COW_UNDER_EDIT, validateAnimal);
}

export default adminSagas;
